import React, { useState, useEffect } from 'react';
import { X, Monitor, Shield, Globe, Lock, FolderOpen, ShieldCheck, DoorOpen } from 'lucide-react';
import api from '../../services/api';

const INITIAL_FORM = {
  marca: 'intelbras',
  modelo: 'automatico',
  nome: '',
  ip: '',
  usuario: '',
  senha: '',
  usa_controle_acesso: true,
  usa_abertura: true,
  abertura_com_video: false,
  abertura_video_rtsp: '',
  sentido: 'entrada',
  grupo_ids: []
};

const DispositivoModal = ({ isOpen, onClose, refreshData, dispositivoParaEditar, grupos }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const isEditing = Boolean(dispositivoParaEditar?.id);

  useEffect(() => {
    if (!isOpen) return;

    if (dispositivoParaEditar) {
      setFormData({
        marca: dispositivoParaEditar.marca || 'intelbras',
        modelo: dispositivoParaEditar.modelo || 'automatico',
        nome: dispositivoParaEditar.nome || '',
        ip: dispositivoParaEditar.ip || '',
        usuario: dispositivoParaEditar.usuario || '',
        senha: dispositivoParaEditar.senha || '',
        usa_controle_acesso: dispositivoParaEditar.usa_controle_acesso !== false,
        usa_abertura: dispositivoParaEditar.usa_abertura !== false,
        abertura_com_video: dispositivoParaEditar.abertura_com_video === true,
        abertura_video_rtsp: dispositivoParaEditar.abertura_video_rtsp || '',
        sentido: dispositivoParaEditar.sentido || 'entrada',
        grupo_ids: dispositivoParaEditar.grupo_ids || []
      });
      return;
    }

    setFormData(INITIAL_FORM);
  }, [isOpen, dispositivoParaEditar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        modelo: formData.modelo || null,
        grupo_ids: formData.grupo_ids.map(Number)
      };

      if (isEditing) {
        await api.put(`/dispositivos/${dispositivoParaEditar.id}`, payload);
        alert("Dispositivo atualizado com sucesso!");
      } else {
        await api.post('/dispositivos/', payload);
        alert("Dispositivo adicionado com sucesso!");
      }

      await refreshData();
      onClose();
    } catch (error) {
      console.error(error);
      const detail = error.response?.data?.detail;
      alert(detail || "Erro ao salvar dispositivo");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-4 md:px-5">
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Monitor size={24} className="text-condo-teal" />
            {isEditing ? 'Configurar Facial' : 'Adicionar Facial'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 md:px-5 md:py-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="space-y-5">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-700">Dados da facial</p>
                <p className="mt-1 text-sm text-slate-500">
                  Preencha os dados principais do equipamento que sera instalado ou configurado.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Marca / Fabricante</label>
                <div className="relative">
                  <Monitor className="absolute left-3 top-3.5 text-gray-400" size={18} />
                  <select
                    required
                    className="w-full border border-gray-300 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-slate-900 outline-none bg-white"
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value, modelo: 'automatico' })}
                  >
                    <option value="intelbras">Intelbras</option>
                    <option value="hikvision">Hikvision</option>
                  </select>
                </div>
              </div>

              {['intelbras', 'hikvision'].includes(formData.marca) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Modelo da facial</label>
                  <select
                    className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-slate-900 outline-none bg-white"
                    value={formData.modelo || 'automatico'}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                  >
                    <option value="automatico">Não especificado / outro modelo</option>
                    {formData.marca === 'intelbras' ? <>
                      <option value="ss1530_1540">SS 1530 / SS 1540</option>
                      <option value="ss3531">SS 3531 (variante não especificada)</option>
                      <option value="ss3531_mf_lite">SS 3531 MF Lite</option>
                    </> : <>
                      <option value="ds-k1t342mwx">DS-K1T342MWX</option>
                      <option value="ds-k1t671mf-l">DS-K1T671MF-L</option>
                      {formData.modelo && !['automatico', 'ds-k1t342mwx', 'ds-k1t671mf-l'].includes(formData.modelo) && <option value={formData.modelo}>{formData.modelo} (não habilitado para importar)</option>}
                    </>}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nome do Dispositivo</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Facial Portaria Principal"
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-slate-900 outline-none"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Endereço do dispositivo</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3.5 text-gray-400" size={18} />
                  <input
                    type="text"
                    required
                    placeholder="192.168.1.100 ou https://facial.exemplo.com"
                    className="w-full border border-gray-300 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-slate-900 outline-none"
                    value={formData.ip}
                    onChange={(e) => setFormData({ ...formData, ip: e.target.value })}
                  />
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label htmlFor="facial-abertura-video" className="mb-2 block text-sm font-semibold text-gray-700">Abertura remota com vídeo?</label>
                <select
                  id="facial-abertura-video"
                  value={formData.abertura_com_video ? 'sim' : 'nao'}
                  onChange={(e) => setFormData({ ...formData, abertura_com_video: e.target.value === 'sim', ...(e.target.value === 'sim' ? { usa_abertura: true } : {}) })}
                  className="w-full rounded-xl border border-gray-300 bg-white p-3 outline-none focus:ring-2 focus:ring-slate-900"
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
                {formData.abertura_com_video && (
                  <div className="mt-3">
                    <label htmlFor="facial-video-rtsp" className="mb-1 block text-sm font-semibold text-gray-700">Link RTSP</label>
                    <input
                      id="facial-video-rtsp"
                      type="text"
                      required
                      maxLength={500}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="rtsp://192.168.1.100:554/stream"
                      value={formData.abertura_video_rtsp}
                      onChange={(e) => setFormData({ ...formData, abertura_video_rtsp: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3 outline-none focus:ring-2 focus:ring-slate-900"
                    />
                    <p className="mt-2 text-xs text-slate-500">Use o link de vídeo da facial ou de uma câmera. Sem credenciais no link, serão usados o usuário e a senha da facial. Credenciais salvas no link ficam ocultas ao editar.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label htmlFor="facial-sentido" className="block text-sm font-semibold text-gray-700 mb-2">Sentido da facial</label>
                <select
                  id="facial-sentido"
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-slate-900 outline-none bg-white"
                  value={formData.sentido}
                  onChange={(e) => setFormData({ ...formData, sentido: e.target.value })}
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Controle de acesso</label>
                <div className="rounded-2xl border border-gray-300 bg-white p-3">
                  <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                    <ShieldCheck size={16} />
                    Usar esta facial para registros e monitoramento de acesso?
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, usa_controle_acesso: true })}
                      className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${formData.usa_controle_acesso ? 'border-condo-teal bg-cyan-50 text-condo-dark' : 'border-gray-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, usa_controle_acesso: false })}
                      className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${!formData.usa_controle_acesso ? 'border-slate-700 bg-slate-100 text-slate-900' : 'border-gray-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      Não
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Abertura</label>
                <div className="rounded-2xl border border-gray-300 bg-white p-3">
                  <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                    <DoorOpen size={16} />
                    Deseja abrir portão com essa facial?
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, usa_abertura: true })}
                      className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${formData.usa_abertura ? 'border-condo-teal bg-cyan-50 text-condo-dark' : 'border-gray-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      Sim
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, usa_abertura: false, abertura_com_video: false })}
                      className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${!formData.usa_abertura ? 'border-slate-700 bg-slate-100 text-slate-900' : 'border-gray-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                    >
                      Não
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Grupos</label>
                <div className="rounded-2xl border border-gray-300 bg-gray-50/70 p-3">
                  <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                    <FolderOpen size={16} />
                    Selecione um ou mais grupos para esta facial
                  </div>
                  <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                    {grupos.length === 0 && (
                      <p className="text-sm text-gray-500">Nenhum grupo criado ainda.</p>
                    )}
                    {grupos.map((grupo) => {
                      const checked = formData.grupo_ids.includes(grupo.id);
                      return (
                        <label
                          key={grupo.id}
                          className="flex cursor-pointer items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-3"
                        >
                          <div>
                            <p className="font-medium text-slate-700">{grupo.nome}</p>
                            <p className="text-xs text-gray-500">{grupo.descricao || 'Sem descrição'}</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setFormData((current) => ({
                                ...current,
                                grupo_ids: checked
                                  ? current.grupo_ids.filter((id) => id !== grupo.id)
                                  : [...current.grupo_ids, grupo.id]
                              }));
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-slate-900 focus:ring-slate-900"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Usuário</label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3.5 text-gray-400" size={18} />
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-slate-900 outline-none"
                      value={formData.usuario}
                      onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
                    <input
                      type="password"
                      className="w-full border border-gray-300 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-slate-900 outline-none"
                      value={formData.senha}
                      onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-slate-800 transition shadow-lg">
                {isEditing ? 'Salvar Configuração' : 'Salvar Dispositivo'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DispositivoModal;
